import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { LangsProvider } from "../../../common/providers/langs-provider";
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UsersProvider } from '../../../users/providers/users-provider';
import { Title } from '@angular/platform-browser';
import { ConfigProvider } from "../../../config-provider.service";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { CurrentUser } from "../../../common/providers/current-user";
import { AthleteRow, AthletesProvider } from '../../providers/athletes-provider';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef, MatDialogTitle
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { User } from "../../../common/models/user";
import { Organization } from "../../../common/models/organization";
import { Lang } from "../../../common/models/lang";
import { Athlete } from "../../../common/models/athlete";
import { Gender } from "../../../common/models/gender";
import { ContractType } from "../../../common/models/contract-type";
import { map, take } from "rxjs/operators";
import { Observable, ReplaySubject, Subject, takeUntil } from "rxjs";
import { MatProgressBar } from "@angular/material/progress-bar";
import { AsyncPipe, NgForOf, NgIf } from "@angular/common";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatOption } from "@angular/material/autocomplete";
import { MatFormField, MatSelect } from "@angular/material/select";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";

@Component({
  selector: 'athletes-coaches-form',
  providers: [],
  styleUrls: ['./coaches.scss'],
  standalone: true,
  imports: [
    MatProgressBar,
    NgIf,
    TranslateModule,
    MatButton,
    MatDialogClose,
    MatDialogActions,
    MatOption,
    NgForOf,
    MatSelect,
    ReactiveFormsModule,
    MatFormField,
    MatDialogContent,
    MatIcon,
    MatTooltip,
    MatIconButton,
    MatDialogTitle,
    NgxMatSelectSearchModule,
    AsyncPipe
  ],
  templateUrl: './coaches.html'
})
export class AthletesCoachesFormComponent implements OnInit, AfterViewInit, OnDestroy {
  public isLoading = false;
  public formTitle = '';
  public confirmButtonTitle = 'Save';
  public organisation: Organization | null = null;
  public athleteId = 0;
  public userId = 0;
  public langId: number = Lang.ID_DEFAULT;

  @ViewChild('multiSelect', { static: true }) multiSelect!: MatSelect;
  public availableCoaches: User[] = [];
  public coachesSelected: User[] = [];
  public coachSelectFormControl: FormControl<User[]> = new FormControl<User[]>([]) as FormControl<User[]>;
  public coachFilterFormControl: FormControl<string> = new FormControl<string>('') as FormControl<string>;
  public filteredCoaches: ReplaySubject<User[]> = new ReplaySubject<User[]>(1);
  protected _onDestroy = new Subject<void>();

  constructor(
    public conf: ConfigProvider,
    public currentUser: CurrentUser,
    public utils: UtilsProvider,
    public dialog: MatDialogRef<AthletesCoachesFormComponent>,
    public usersProvider: UsersProvider,
    private _toaster: MatSnackBar,
    private _title: Title,
    private _athletesProvider: AthletesProvider,
    private _router: Router,
    private _route: ActivatedRoute,
    private _langsProvider: LangsProvider,
    private _translater: TranslateService,
    @Inject(MAT_DIALOG_DATA) private _data : any
  ) {
    this.availableCoaches = this._data.availableCoaches;
    this.organisation = _data.organization;

    if (this._data.athleteRow) {
      this.athleteId = this._data.athleteRow.athlete.id;
      this.userId = this._data.athleteRow.user.id;
      this.langId = this._data.athleteRow.user.langId;
      this.coachesSelected = this._data.athleteRow.coaches;
    }

    this.formTitle = this._data.formTitle;
    this.confirmButtonTitle = this._data.confirmButtonTitle;
  }


  public ngOnInit() {
    // Charger les coachs filtrés initialement
    this.filteredCoaches.next(this.availableCoaches.slice());

    // Écouter les changements dans le champ de recherche
    this.coachFilterFormControl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterCoaches();
      });

    // Initialiser les coachs sélectionnés si nécessaire
    if (this.coachesSelected.length > 0) {
      this.coachSelectFormControl.setValue(
        this.coachesSelected
      );
    }
  }

  public ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  public ngAfterViewInit() {
    this.setInitialValue();
  }

  public onSaveClicked(): void {
    if (this.isValid()) {
      this.isLoading = true;
      this.save().subscribe({
        next: (result: AthleteRow) => {
          this._toaster.open(this._translater.instant('Athlete_saved'), '', { duration: 5000 });
          this.isLoading = false;
          this.dialog.close(result);
        },
        error: (error: any) => this.onError(error)
      })
    } else {
      this.touchStepFields();
      this._toaster.open(this._translater.instant('Correct_form_errors'), '', { duration: 5000 });
    }
  }

  private isValid(): boolean {
    return !this.coachSelectFormControl.invalid;
  }

  private touchStepFields(): void {
    this.coachSelectFormControl.markAsTouched();
  }

  private save(): Observable<AthleteRow> {
    const athleteRow: AthleteRow = {
      user: new User({}),
      athlete: new Athlete({}),
      gender: {} as Gender,
      contractType: {} as ContractType,
      addresses: [],
      coaches: [],
      coachNames: '',
      teams: [],
      teamNames: '',
      rights: undefined,
    };

    athleteRow.athlete.id = this.athleteId;
    athleteRow.user.id = this.userId;

    // coach
    if (this.coachSelectFormControl.value.length) {
      for (let i = 0; i < this.coachSelectFormControl.value.length; i++) {
        const coachUser = this.utils.findIn(
          this.coachSelectFormControl.value[i].id,
          this.availableCoaches,
          'id');
        if (coachUser) {
          athleteRow.coaches.push(coachUser);
        }
      }
    }

    return this._athletesProvider.saveAthleteCoaches(this.organisation?.id!, athleteRow);
  }

  protected setInitialValue() {
    this.filteredCoaches
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.multiSelect.compareWith = (a: User, b: User) => a && b && a.id === b.id;
      });
  }

  protected filterCoaches() {
    if (!this.availableCoaches) {
      return;
    }
    // get the search keyword
    let search = this.coachFilterFormControl.value;
    if (!search) {
      this.filteredCoaches.next(this.availableCoaches.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the banks
    this.filteredCoaches.next(
      this.availableCoaches.filter(coach => `${coach.firstName} ${coach.lastName}`.toLowerCase().indexOf(search) > -1)
    );
  }

  private onError(error: any): void {
    this.isLoading = false;

    if (error.status === 422) {
      if (error.error.errors && error.error.errors.email) {
        // c'est qu'on a essayé de faire valider un email test@test par exemple
        // (c'est validé par le validator angular mais pas laravel...)
        return;
      }
    }

    this._toaster.open(this._translater.instant('Error_unknown'), '', { duration: 5000 });
  }

}
